// WishDetail's block renderers (W37.0 §4.8, from `routes/WishDetail.tsx`).
//
// The route file was 900 lines / 31.5 KB against §3 #10's 15 KB ceiling, and
// two thirds of it was these: six block-type renderers, their sub-table bodies,
// and the list that picks between them. The page component is the part that
// fetches and routes; this is the part that draws, and the two change for
// different reasons — a new block type touches this file, a new query shape
// touches the page.
//
// Moved verbatim, no behaviour change (W37.0 §4.0). The import block is the
// original one; `eslint` prunes what this file does not use.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { PhotoBlock } from "./WishPhotoBlock";
// W50.3 D-2 — success toasts for the block-level CRUD. Failure stays with the
// app-wide reporter (`main.tsx` → `defaultMutationOnError` + `<MutationErrorReporter />`).
import { useToast } from "@/components/ui/toast";
import { Card } from "@/components/ui/card";
import {
  addChecklistItem,
  patchChecklistItem,
  deleteChecklistItem,
  addLinkItem,
  deleteLinkItem,
  listChecklistItems,
  listLinkItems,
  type ChecklistItemOut,
  type LinkItemOut,
} from "@/lib/api/wishBlocks";
import {
  type WishBlockOut,
  type WishOut,
} from "@/lib/api/wishlist";
import { cn } from "@/lib/utils/cn";
import { blockTypeLabel } from "@/lib/wish/blockTypeLabel";
import { queryKeys } from "@/lib/queries/keys";

// Background weight per priority, so a `must` block reads first on a phone
// where the strip is the only thing above the fold.
const PRIORITY_STRIP: Record<string, string> = {
  must: "bg-paper-deep",
  want: "bg-paper-calm",
  maybe: "bg-hairline",
};

export function BlockList({
  wish,
  highlightBlockId,
  onOpenLogSheet,
  isMobile,
}: {
  wish: WishOut;
  highlightBlockId: string | null;
  onOpenLogSheet: (block: WishBlockOut) => void;
  isMobile: boolean;
}) {
  if (wish.blocks.length === 0) {
    return (
      <p
        data-testid="wish-detail-no-blocks"
        className="text-body text-ink-3 py-s-6 text-center"
      >
        這個心願還沒有區塊。
      </p>
    );
  }
  return (
    <ol
      data-component="WishDetailBlockList"
      className="flex flex-col gap-s-3"
    >
      {wish.blocks.map((block) => (
        <li
          key={block.id}
          data-component="WishDetailBlock"
          data-block-type={block.block_type}
          data-block-anchor={block.id}
          data-testid={`wish-detail-block-${block.id}`}
          className={cn(
            "relative pl-3 transition-shadow",
            highlightBlockId === block.id &&
              "ring-2 ring-ink-1/40 rounded-2xl",
          )}
        >
          <span
            aria-hidden
            className={cn(
              "absolute left-0 top-0 bottom-0 w-1 rounded-full",
              PRIORITY_STRIP[
                block.block_type === "photo" ? "maybe" : block.block_type
              ] ?? "bg-hairline",
            )}
          />
          <BlockRenderer
            block={block}
            onOpenLogSheet={isMobile ? onOpenLogSheet : undefined}
            isMobile={isMobile}
          />
        </li>
      ))}
    </ol>
  );
}

function BlockRenderer({
  block,
  onOpenLogSheet,
  isMobile,
}: {
  block: WishBlockOut;
  /** Tap handler for budget / progress blocks on mobile (W34.2.0). */
  onOpenLogSheet?: (block: WishBlockOut) => void;
  isMobile: boolean;
}) {
  const tappable =
    isMobile &&
    (block.block_type === "progress" || block.block_type === "budget");
  const onTap = () => {
    if (tappable && onOpenLogSheet) onOpenLogSheet(block);
  };
  const role = tappable ? "button" : undefined;
  return (
    // W53.2 D-1/D-2 — this was a hand-written `surface-plain border
    // border-hairline rounded-2xl p-s-4` with **no shadow**, sitting one screen
    // away from two sibling cards on the same page that had one. The classes
    // matched `Card`'s default tier on three of four axes, which is the worst
    // case: close enough that nobody reconciled them.
    <Card
      as="article"
      data-testid={`block-renderer-${block.id}`}
      data-tappable={tappable ? "true" : "false"}
      role={role}
      tabIndex={tappable ? 0 : undefined}
      onClick={onTap}
      onKeyDown={(e) => {
        if (tappable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onTap();
        }
      }}
      className={cn(
        "p-s-4 flex flex-col gap-s-2",
        // Kept as-is rather than normalised to `hover:bg-black/5`: it reads as
        // `bg-paper-calm` on the paper ground, and `surface-plain` is an
        // **unlayered** rule so a Tailwind utility on this same element loses to
        // it either way. See the `p-s-4`-on-the-primitive note in `card.tsx`.
        tappable && "cursor-pointer press hover:bg-paper-calm",
      )}
    >
      <header className="flex items-center justify-between gap-s-2">
        <h3 className="font-display text-body font-semibold text-ink-1 truncate">
          {block.title || blockTypeLabel(block.block_type)}
        </h3>
        <span className="text-meta text-ink-3">{blockTypeLabel(block.block_type)}</span>
      </header>
      <BlockBody block={block} />
    </Card>
  );
}

function BlockBody({ block }: { block: WishBlockOut }) {
  switch (block.block_type) {
    case "notes":
      return (
        <p className="text-body text-ink-1 whitespace-pre-wrap">
          {(block.data?.body as string) || (
            <span className="text-ink-3">（無內容）</span>
          )}
        </p>
      );
    case "checklist":
      return <ChecklistBlockBody block={block} />;
    case "progress":
      return <ProgressBlockBody block={block} />;
    case "budget":
      return <BudgetBlockBody block={block} />;
    case "link":
      return <LinkBlockBody block={block} />;
    case "photo":
      return <PhotoBlock block={block} />;
    default:
      return (
        <p className="text-meta text-ink-3">
          未支援的區塊類型：{blockTypeLabel(block.block_type)}
        </p>
      );
  }
}

// --- progress / budget block bodies (W34.2.0 + tap affordance) --------

function ProgressBlockBody({ block }: { block: WishBlockOut }) {
  const mode = (block.data?.mode as string) ?? "percent";
  const targetAmount = block.data?.target_amount as number | undefined;
  const percent = Number(block.progress_summary?.percent ?? 0);
  return (
    <div
      data-testid={`progress-block-body-${block.id}`}
      className="flex flex-col gap-s-1"
    >
      <p className="text-meta text-ink-2">
        模式：{mode === "amount" ? "金額" : "百分"}
        {mode === "amount" && targetAmount
          ? ` · 目標 ${targetAmount.toLocaleString()}`
          : null}
      </p>
      <div className="h-1.5 rounded-full bg-hairline overflow-hidden">
        <div
          className="h-full bg-ink-1 transition-all duration-280"
          style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
        />
      </div>
      <p className="text-meta text-ink-3 tabular-nums">{percent}%</p>
    </div>
  );
}

function BudgetBlockBody({ block }: { block: WishBlockOut }) {
  const amount = block.data?.amount as number | undefined;
  const currency = String(block.data?.currency ?? "TWD");
  const spent = Number(block.budget_summary?.spent ?? 0);
  const percent = Number(block.progress_summary?.percent ?? 0);
  return (
    <div
      data-testid={`budget-block-body-${block.id}`}
      className="flex flex-col gap-s-1"
    >
      <p className="text-meta text-ink-2">
        上限 {amount ? amount.toLocaleString() : "—"} {currency} · 已花{" "}
        {spent.toLocaleString()}
      </p>
      <div className="h-1.5 rounded-full bg-hairline overflow-hidden">
        <div
          className="h-full bg-ink-1 transition-all duration-280"
          style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
        />
      </div>
    </div>
  );
}

// --- checklist / link / photo block bodies (W34.2.1) ------------------

function ChecklistBlockBody({ block }: { block: WishBlockOut }) {
  return <ChecklistBlock block={block} />;
}

function LinkBlockBody({ block }: { block: WishBlockOut }) {
  return <LinkBlock block={block} />;
}


// --- checklist renderer (typed wrapper over lib/api/wishlist) ---------

function ChecklistBlock({ block }: { block: WishBlockOut }) {
  const wishId = block.wish_id;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [newContent, setNewContent] = useState("");
  const list = useQuery({
    queryKey: ["wishes", "detail", wishId, "blocks", block.id, "checklist"],
    queryFn: () => listChecklistItems(wishId, block.id),
    enabled: true,
    staleTime: 30_000,
  });
  const addMut = useMutation({
    mutationFn: async (content: string) =>
      addChecklistItem(wishId, block.id, { content }),
    onSuccess: () => {
      setNewContent("");
      // W50.3 D-2 — success only; failure is the app-wide reporter's job.
      toast.push({ message: "已新增清單項目", variant: "you" });
      void queryClient.invalidateQueries({
        queryKey: [
          "wishes",
          "detail",
          wishId,
          "blocks",
          block.id,
          "checklist",
        ],
      });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
  const toggleMut = useMutation({
    mutationFn: async (input: { itemId: string; isChecked: boolean }) =>
      patchChecklistItem(wishId, block.id, input.itemId, {
        is_checked: input.isChecked,
      }),
    onSuccess: () => {
      // W50.3 D-2 — a checkbox flipping with no acknowledgement is the least
      // trustworthy interaction on the screen: it looks identical whether the
      // write landed or not.
      toast.push({ message: "已更新項目", variant: "you" });
      void queryClient.invalidateQueries({
        queryKey: [
          "wishes",
          "detail",
          wishId,
          "blocks",
          block.id,
          "checklist",
        ],
      });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
  const delMut = useMutation({
    mutationFn: async (itemId: string) =>
      deleteChecklistItem(wishId, block.id, itemId),
    onSuccess: () => {
      toast.push({ message: "已刪除項目", variant: "you" });
      void queryClient.invalidateQueries({
        queryKey: [
          "wishes",
          "detail",
          wishId,
          "blocks",
          block.id,
          "checklist",
        ],
      });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
  const items: ChecklistItemOut[] = list.data?.items ?? [];
  return (
    <div
      data-testid={`checklist-block-${block.id}`}
      className="flex flex-col gap-s-2"
    >
      {list.isLoading ? (
        <p className="text-meta text-ink-3">載入中…</p>
      ) : items.length === 0 ? (
        <p className="text-meta text-ink-3">（尚無待辦）</p>
      ) : (
        <ul className="flex flex-col">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`checklist-item-${it.id}`}
              className="flex items-center gap-2 py-s-1 border-b border-hairline last:border-b-0"
            >
              <input
                type="checkbox"
                checked={it.is_checked}
                disabled={toggleMut.isPending}
                onChange={(e) =>
                  toggleMut.mutate({
                    itemId: it.id,
                    isChecked: e.target.checked,
                  })
                }
                data-testid={`checklist-toggle-${it.id}`}
                className="h-4 w-4 accent-ink-1"
              />
              <span
                className={cn(
                  "flex-1 text-body-sm text-ink-1",
                  it.is_checked && "line-through text-ink-3",
                )}
              >
                {it.content}
              </span>
              <button
                type="button"
                aria-label="刪除"
                onClick={() => delMut.mutate(it.id)}
                disabled={delMut.isPending}
                data-testid={`checklist-delete-${it.id}`}
                className="press text-meta text-ink-3 hover:text-ink-1 disabled:opacity-50"
              >
                刪除
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const v = newContent.trim();
          if (!v) return;
          addMut.mutate(v);
        }}
      >
        <input
          type="text"
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          maxLength={200}
          placeholder="新增待辦…"
          data-testid={`checklist-input-${block.id}`}
          className="flex-1 h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1 text-body-sm"
        />
        <button
          type="submit"
          disabled={!newContent.trim() || addMut.isPending}
          data-testid={`checklist-add-${block.id}`}
          className="press min-h-10 px-3 rounded-full bg-ink-1 text-paper text-body-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1"
        >
          <Plus className="h-4 w-4" aria-hidden />
          新增
        </button>
      </form>
    </div>
  );
}

// --- link block renderer (W34.2.1) ------------------------------------

function LinkBlock({ block }: { block: WishBlockOut }) {
  const wishId = block.wish_id;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [newUrl, setNewUrl] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const list = useQuery({
    queryKey: ["wishes", "detail", wishId, "blocks", block.id, "links"],
    queryFn: () => listLinkItems(wishId, block.id),
    staleTime: 30_000,
  });
  const addMut = useMutation({
    mutationFn: async () =>
      addLinkItem(wishId, block.id, {
        url: newUrl.trim(),
        title: newTitle.trim() || newUrl.trim(),
      }),
    onSuccess: () => {
      setNewUrl("");
      setNewTitle("");
      toast.push({ message: "已新增連結", variant: "you" });
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "blocks", block.id, "links"],
      });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
  const delMut = useMutation({
    mutationFn: async (itemId: string) =>
      deleteLinkItem(wishId, block.id, itemId),
    onSuccess: () => {
      toast.push({ message: "已刪除連結", variant: "you" });
      void queryClient.invalidateQueries({
        queryKey: ["wishes", "detail", wishId, "blocks", block.id, "links"],
      });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "detail"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
  const items: LinkItemOut[] = list.data ?? [];
  return (
    <div
      data-testid={`link-block-${block.id}`}
      className="flex flex-col gap-s-2"
    >
      {list.isLoading ? (
        <p className="text-meta text-ink-3">載入中…</p>
      ) : items.length === 0 ? (
        <p className="text-meta text-ink-3">（尚無連結）</p>
      ) : (
        <ul className="flex flex-col">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`link-item-${it.id}`}
              className="flex items-center gap-2 py-s-1 border-b border-hairline last:border-b-0"
            >
              <a
                href={it.url}
                target="_blank"
                rel="noopener noreferrer"
                className="press flex-1 min-w-0 text-body-sm text-ink-1 underline truncate"
              >
                {it.title}
              </a>
              <button
                type="button"
                aria-label="刪除"
                onClick={() => delMut.mutate(it.id)}
                disabled={delMut.isPending}
                data-testid={`link-delete-${it.id}`}
                className="press text-meta text-ink-3 hover:text-ink-1 disabled:opacity-50"
              >
                刪除
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-col gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newUrl.trim()) return;
          addMut.mutate();
        }}
      >
        <input
          type="url"
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="https://…"
          data-testid={`link-url-input-${block.id}`}
          className="w-full h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1 text-body-sm"
        />
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="標題（選填）"
            data-testid={`link-title-input-${block.id}`}
            className="flex-1 h-10 px-3 rounded-md border border-hairline bg-paper-elevated text-ink-1 text-body-sm"
          />
          <button
            type="submit"
            disabled={!newUrl.trim() || addMut.isPending}
            data-testid={`link-add-${block.id}`}
            className="press min-h-10 px-3 rounded-full bg-ink-1 text-paper text-body-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1"
          >
            <Plus className="h-4 w-4" aria-hidden />
            新增
          </button>
        </div>
      </form>
    </div>
  );
}

// --- photo block renderer (W34.2.1) -----------------------------------


// --- Linked diaries -----------------------------------------------------
